import { useMemo } from 'react';
import { TableHeaderType } from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import {
  ButtonIcon,
  Icon,
  Stack,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { Outage } from '@/apis/outages/types/outagesApi';
import { SortConfig } from '@/hooks';

const RISK_TOOLTIP_TEXT = `The risk level shown here is relative, not absolute.

Each active outage is compared against all other current outages. Based on this comparison, every outage is assigned to a risk category — from Extreme to Low.

This normalization ensures that all risk categories are represented, helping you quickly see which outages are most critical compared to the rest.`;

export const useOutagesTableHeaders = (
  isSmallScreen: boolean,
  sortConfig: SortConfig<Outage> | null,
  onSort: (key: keyof Outage) => void,
): TableHeaderType[] => {
  const handleSort = (key: keyof Outage) => () => {
    onSort(key);
  };

  const riskHeaderTitle = (
    <Stack direction="row" horizontalAlign="center" gap={8}>
      <Typography tag="textdefault_strong">Risk Factor</Typography>
      <ButtonIcon
        icon={
          sortConfig?.key === 'outageNormalized0_100'
            ? sortConfig?.direction === 'desc'
              ? 'IconSortAscending'
              : 'IconSortDescending'
            : 'IconSort'
        }
        onClick={handleSort('outageNormalized0_100')}
      />
      <Tooltip title={RISK_TOOLTIP_TEXT} arrow>
        <Icon
          icon="IconInformation"
          color="var(--color-default)"
          iconSize={0.8}
        />
      </Tooltip>
    </Stack>
  );

  return useMemo(
    () =>
      [
        {
          title: 'Outage ID',
          align: 'left',
          columnKey: 'outageId',
          columnWidth: '100px',
        },

        ...(!isSmallScreen
          ? [
              {
                title: 'Site Name',
                align: 'left',
                columnKey: 'siteName',
                columnWidth: '150px',
              },
            ]
          : []),
        {
          title: 'Outage Name',
          align: 'left',
          columnKey: 'outageName',
        },
        ...(isSmallScreen
          ? [
              {
                title: riskHeaderTitle,
                align: 'center',
                columnKey: 'outageNormalized0_100',
                columnWidth: '200px',
              },
            ]
          : []),
        ...(!isSmallScreen
          ? [
              {
                title: 'Millwrights',
                align: 'center',
                columnKey: 'workerCount',
                columnWidth: '150px',
              },
            ]
          : []),
        ...(!isSmallScreen
          ? [
              {
                title: (
                  <Stack direction="row" gap={8}>
                    <Typography tag="textdefault_strong">Start Date</Typography>
                    <ButtonIcon
                      icon={
                        sortConfig?.key === 'outageStart'
                          ? sortConfig?.direction === 'desc'
                            ? 'IconSortAscending'
                            : 'IconSortDescending'
                          : 'IconSort'
                      }
                      onClick={handleSort('outageStart')}
                    />
                  </Stack>
                ),
                align: 'left',
                columnKey: 'outageStart',
                columnWidth: '150px',
              },
            ]
          : []),
        ...(!isSmallScreen
          ? [
              {
                title: 'End Date',
                align: 'left',
                columnKey: 'outageEnd',
                columnWidth: '120px',
              },
            ]
          : []),
        ...(!isSmallScreen
          ? [
              {
                title: riskHeaderTitle,
                align: 'center',
                columnKey: 'outageNormalized0_100',
                columnWidth: '250px',
              },
            ]
          : []),
      ] as TableHeaderType[],
    [isSmallScreen, sortConfig, onSort],
  );
};
